// useProfileCard — fetch + cache wrapper around the existing public
// profile endpoints, returning a card-shaped payload for the chat
// preview component.
//
// We REUSE the same APIs that the full PlayerProfile / CoachProfile
// pages already consume:
//
//   playerService.getPublicPlayerProfile(wyscoutId)
//     → GET /dashboard/players/public/<wyscoutId>/
//
//   playerService.getPublicCoachProfile(coachId)
//     → GET /users/public/profile/coach/<coachId>/
//
// Why no dedicated backend endpoint? Both above endpoints already return
// every field a card needs (name, photo, position/role, club, country,
// age, market value, etc.) — projection happens client-side. Keeping the
// data contract single-source-of-truth means a Wyscout/SportMonks
// upstream change shows in the card without any backend rev.
//
// Caching strategy:
//   - Redux `profileCards.byKey['<type>:<id>']`
//   - 5 minute TTL (CHAT_CACHE_TTL)
//   - In-flight requests deduplicated via the `loading` status
//
// The card mapping is intentionally defensive — the same player payload
// has been observed as `{ ...player }`, `{ player: { ... } }` (nested),
// and `{ results: { ... } }` (envelope). We probe each shape and bail
// to a notfound state when nothing useful resolves.

import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { playerService } from "../services/api";
import {
  profileCardLoading,
  profileCardReady,
  profileCardNotFound,
  profileCardError,
} from "../store/slices/profileCardsSlice";

const CHAT_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// -----------------------------------------------------------------------------
// Mappers — turn raw API payloads into the props the design-bundle cards expect.
// Each helper returns null when the payload is so empty there's nothing to show
// (caller flips that to "notfound").
// -----------------------------------------------------------------------------

function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

// Currency symbol map for the few currencies the backend actually
// stores (EUR / USD / GBP). Any unknown currency falls back to the
// ISO code with a trailing space so the result is still readable
// rather than blank.
const CURRENCY_SYMBOL = { EUR: "€", USD: "$", GBP: "£" };

function formatMarketValue(raw, currency = "EUR") {
  if (raw == null || raw === "") return null;
  const n =
    typeof raw === "number"
      ? raw
      : Number(String(raw).replace(/[^\d.]/g, ""));
  if (!isFinite(n) || n <= 0) return null;
  const sym = CURRENCY_SYMBOL[(currency || "EUR").toUpperCase()] ||
    `${currency} `;
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${sym}${(n / 1_000).toFixed(0)}K`;
  return `${sym}${n}`;
}

// Resolve a market-value pair from the various shapes the player
// public-profile endpoint emits today and has emitted historically:
//
//   { market_value: { value: 105000, currency: "EUR" } }   <- current
//   { market_value: { sportdevs_value: 105000, ... } }     <- older
//   { market_value: 105000 }                                <- flat numeric
//   { ts_market_value: 105000, ts_market_value_currency: "EUR" }
//
// Returns { amount, currency } or null if no usable value is present.
function resolveMarketValue(r, nested) {
  // Prefer the wrapper object on results.market_value (current shape).
  const mvObj = r?.market_value;
  if (mvObj && typeof mvObj === "object") {
    const amount =
      mvObj.value ?? mvObj.sportdevs_value ?? mvObj.amount ?? null;
    if (amount != null) {
      return { amount, currency: mvObj.currency || "EUR" };
    }
  }
  if (typeof mvObj === "number" && mvObj > 0) {
    return { amount: mvObj, currency: "EUR" };
  }
  // Last-resort: raw ts_market_value on the nested player payload.
  const raw = nested?.ts_market_value ?? r?.ts_market_value;
  if (raw && Number(raw) > 0) {
    return {
      amount: raw,
      currency:
        nested?.ts_market_value_currency ||
        r?.ts_market_value_currency ||
        "EUR",
    };
  }
  return null;
}

function mapPlayer(raw) {
  if (!raw || typeof raw !== "object") return null;
  // The /players/public/<id>/ response is sometimes wrapped in `results`
  // (PlayerProfile.js handles that shape too).
  const r = raw.results || raw.data?.results || raw.data || raw;
  const nested = r.player || r;

  const name =
    nested.name ||
    r.name ||
    [nested.firstname, nested.lastname].filter(Boolean).join(" ").trim() ||
    null;
  if (!name) return null;

  // Photo: wyscout image → player_image → profile_image
  const photoUrl =
    nested.image_url ||
    r.image_url ||
    r.image_path ||
    r.profile_image ||
    nested.player_image ||
    null;

  // Position primary + secondary (tp_detailed_position is an array of
  // { name, code } in the live API).
  const primary =
    r.tp_detailed_position?.[0]?.name ||
    nested.tp_detailed_position?.[0]?.name ||
    nested.position ||
    r.position ||
    r.role?.name ||
    nested.role?.name ||
    null;
  const secondary =
    r.tp_detailed_position?.[1]?.name ||
    nested.tp_detailed_position?.[1]?.name ||
    null;

  // Club — possibly under `team` (Wyscout response) or `current_club`.
  const team = r.team || nested.team || nested.current_club || null;
  const club = team
    ? {
        name: team.name || team.short_name || null,
        logo_url: team.image_url || team.image_path || team.logo || null,
      }
    : null;

  // Nationality + flag: Wyscout returns { id, name, image_url }; sometimes
  // plain string. Flag URL is always preferred over emoji because we already
  // serve SVGs from the country_flag_helper everywhere else in the app.
  const natRaw = nested.nationality || r.nationality;
  let nationality = null;
  if (natRaw && typeof natRaw === "object") {
    nationality = {
      name: natRaw.name || natRaw.country || null,
      flag_url: natRaw.image_url || null,
    };
  } else if (typeof natRaw === "string" && natRaw) {
    nationality = { name: natRaw, flag_url: null };
  }
  // All citizenships (L-FE-1): the ordered `nationalities[]` when the payload has it, else the single one.
  const natListRaw = Array.isArray(nested.nationalities) && nested.nationalities.length
    ? nested.nationalities
    : Array.isArray(r.nationalities) && r.nationalities.length ? r.nationalities : [];
  const nationalities = natListRaw.length
    ? natListRaw
        .map((n) => (n && typeof n === "object" ? { name: n.name || n.country || null, flag_url: n.image_url || n.flag_url || null } : typeof n === "string" && n ? { name: n, flag_url: null } : null))
        .filter((n) => n && n.name)
    : nationality ? [nationality] : [];

  return {
    type: "player",
    nationalities,
    id: r.wyscout_id || r.id || nested.wyscout_id || nested.id,
    name,
    photo_url: photoUrl,
    position: primary,
    secondary_position: secondary,
    jersey_number: r.current_shirt_number || nested.current_shirt_number || null,
    club,
    nationality,
    age: calcAge(nested.date_of_birth || r.date_of_birth),
    // Height: backend exposes `height` (resolves to height_cm in the
    // PlayerPublicProfile serializer). Some responses also include
    // a raw `height_cm` field, so check both. Numeric expected.
    height: nested.height || r.height || nested.height_cm || r.height_cm || null,
    foot: nested.foot || r.foot || null,
    // Market value handles every shape the backend has emitted (object
    // with .value/.sportdevs_value/.amount, flat number, or the raw
    // ts_market_value field). See resolveMarketValue above for the
    // full precedence order.
    market_value: (() => {
      const mv = resolveMarketValue(r, nested);
      return mv ? formatMarketValue(mv.amount, mv.currency) : null;
    })(),
    verified: Boolean(nested.verified || r.verified),
    profile_url: `/search/player/${r.wyscout_id || r.id || nested.wyscout_id || nested.id}`,
  };
}

function mapCoach(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data || raw.results || raw;

  const name = r.name || r.display_name || null;
  if (!name) return null;

  const photoUrl = r.logo || r.profile_picture_url || r.image_path || null;

  // TSCoach.type: 1 = Head coach, 2 = Interim.
  let role = "Coach";
  if (r.type === 1 || r.type === "1") role = "Head Coach";
  else if (r.type === 2 || r.type === "2") role = "Interim Coach";
  else if (r.role) role = r.role;

  const team = r.team || null;
  const club = team
    ? {
        name: team.name || team.short_name || null,
        logo_url: team.logo || team.image_path || team.image_url || null,
      }
    : null;

  const natRaw = r.nationality;
  let nationality = null;
  if (natRaw && typeof natRaw === "object") {
    nationality = {
      name: natRaw.name || null,
      flag_url: natRaw.image_url || null,
    };
  } else if (typeof natRaw === "string" && natRaw) {
    nationality = { name: natRaw, flag_url: null };
  }

  return {
    type: "coach",
    id: r.id,
    name,
    photo_url: photoUrl,
    role,
    formation: r.preferred_formation || null,
    license: r.license_number || null,            // populated only if Platform Coach is linked
    club,
    nationality,
    age: r.age || calcAge(r.birthday) || null,
    verified: Boolean(r.verified),
    // Platform number when the payload ships it (URL-Umstellung 2026-08-24);
    // the TS uuid stays accepted server-side and canonicalizes on landing.
    profile_url: `/search/coach/${r.public_coach_id || r.id}`,
  };
}

// -----------------------------------------------------------------------------
// Public hook
// -----------------------------------------------------------------------------

export default function useProfileCard(type, id) {
  const dispatch = useDispatch();
  const key = id ? `${type}:${id}` : null;
  const entry = useSelector((state) =>
    key ? state.profileCards.byKey[key] : null,
  );

  useEffect(() => {
    if (!key || !type || !id) return;

    // Re-use a fresh cache entry without re-fetching.
    if (entry) {
      const fresh = Date.now() - entry.fetchedAt < CHAT_CACHE_TTL_MS;
      const okStatus = entry.status === "ready" || entry.status === "notfound";
      if (fresh && okStatus) return;
      // Skip re-fetching while another caller is already loading the same key.
      if (entry.status === "loading") return;
    }

    let cancelled = false;
    dispatch(profileCardLoading(key));

    const fetcher =
      type === "player"
        ? playerService.getPublicPlayerProfile
        : playerService.getPublicCoachProfile;

    (async () => {
      try {
        const res = await fetcher(id);
        if (cancelled) return;

        if (!res?.success) {
          // Backend reachable but profile not available — surface as 'notfound'
          // so the renderer can fall back to a plain link.
          dispatch(profileCardNotFound(key));
          return;
        }
        const mapped = type === "player" ? mapPlayer(res.data) : mapCoach(res.data);
        if (!mapped) {
          dispatch(profileCardNotFound(key));
          return;
        }
        dispatch(profileCardReady({ key, data: mapped }));
      } catch (err) {
        if (cancelled) return;
        dispatch(profileCardError({ key, error: err?.message }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // entry intentionally omitted — we don't want stale-entry refetches looping
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, type, id, dispatch]);

  return useMemo(
    () => ({
      data: entry?.data || null,
      status: entry?.status || (key ? "loading" : "idle"),
      error: entry?.error || null,
    }),
    [entry, key],
  );
}
